import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { NavLink as RouterLink, matchPath, useLocation } from 'react-router-dom';
import { alpha, useTheme, styled } from '@mui/material/styles';
import { ChevronRight } from '@mui/icons-material'; // Add arrow icon
import { 
  Box,
  List,
  Collapse,
  ListItemText,
  ListItemIcon,
  ListItemButton,
  Popover,
  Tooltip,
} from '@mui/material';
import Iconify from './components/Iconify';
import { prepareSessionHandoff } from './utils/storageProxy';
import { isPageAllowedForClient } from './utils/clientRouteAccess';

const ListItemStyle = styled((props) => <ListItemButton disableGutters {...props} />)(({ theme }) => ({
  ...theme.typography.body2,
  height: 42,
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  textTransform: 'capitalize',
  color: theme.palette.text.secondary,
  borderRadius: 8,
  marginBottom: 4,
  paddingLeft: theme.spacing(1.25),
  paddingRight: theme.spacing(1.25),
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
    color: theme.palette.text.primary,
  },
}));

const ListItemIconStyle = styled(ListItemIcon)({
  width: 20,
  height: 20,
  color: 'inherit',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: 20,
  marginRight: 10,
});

function NavItem({ item, active, userType, isCollapsed }) {
  const theme = useTheme();
  const isActiveRoot = active(item.path);
  const { title, path, icon, info, children, isHideInXs, disabledMessage, openNewTab } = item;
  const isDisabled = Boolean(disabledMessage);
  const isActiveChild = Boolean(children?.some((child) => active(child.path)));
  const isParentActive = isActiveRoot || isActiveChild;

  const [open, setOpen] = useState(isActiveRoot || isActiveChild);
  const [anchorEl, setAnchorEl] = useState(null);

  useEffect(() => {
    if (isParentActive) {
      setOpen(true);
    }
  }, [isParentActive]);

  const handleOpen = () => setOpen((prev) => !prev);
  const triggerSessionHandoff = () => {
    prepareSessionHandoff();
  };
  const handleOpenNewTab = (event, nextPath, callback) => {
    if (!nextPath) return;

    const isPlainLeftClick =
      event.button === 0 &&
      !event.metaKey &&
      !event.ctrlKey &&
      !event.shiftKey &&
      !event.altKey;

    if (isPlainLeftClick) {
      event.preventDefault();
      prepareSessionHandoff();
      window.open(nextPath, '_blank', 'noopener,noreferrer');
    } else {
      triggerSessionHandoff();
    }

    if (callback) {
      callback();
    }
  };
  //const handlePopoverOpen = (event) => setAnchorEl(event.currentTarget);
  const handlePopoverClose = () => setAnchorEl(null);
  //console.log("=====children==",children);
  const activeRootStyle = {
    color: theme.palette.primary.main,
    fontWeight: 600,
    bgcolor: alpha(theme.palette.primary.main, theme.palette.action.selectedOpacity),
    border: `1px solid ${alpha(theme.palette.primary.main, 0.26)}`,
  };

  const activeSubStyle = {
    color: theme.palette.text.primary,
    fontWeight: 600,
    bgcolor: theme.palette.action.hover,
  };

  const checkHideInXs = (isHide) => (isHide ? { display: { xs: 'none', sm: 'flex' } } : {});

  const withDisabledTooltip = (content) =>
    isDisabled ? (
      <Tooltip title={disabledMessage} placement="right" arrow>
        <Box component="span" sx={{ display: 'block', cursor: 'not-allowed' }}>
          {content}
        </Box>
      </Tooltip>
    ) : (
      content
    );


const handlePopoverToggle = (event) => {
  if (anchorEl) {
    setAnchorEl(null);
  } else {
    setAnchorEl(event.currentTarget);
  }
};


  if (children) {
    if (isCollapsed) {
      return (
        <>
{withDisabledTooltip(<Box onClick={isDisabled ? undefined : handlePopoverToggle}>
  <ListItemButton
    disabled={isDisabled}
    aria-label={isDisabled ? `${title}: ${disabledMessage}` : title}
    sx={{
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      textAlign: 'center',
      px: 1,
      py: 1.5,
      width: 64,
      minHeight: 72,
      borderRadius: 2,
      color: isParentActive ? 'primary.main' : 'text.secondary',
      bgcolor: isParentActive ? 'action.selected' : 'transparent',
      border: isParentActive ? `1px solid ${alpha(theme.palette.primary.main, 0.3)}` : '1px solid transparent',
      '&:hover': {
        bgcolor: isParentActive ? 'action.selected' : 'action.hover',
      },
    }}
  >
    <ListItemIcon
      sx={{
        mb: 0.5,
        minWidth: 'unset',
        color: 'inherit',
        justifyContent: 'center',
      }}
    >
      {icon}
    </ListItemIcon>

    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 0.25,
      }}
    >
      <ListItemText
        primary={title}
        primaryTypographyProps={{
          fontSize: 11,
          fontWeight: 500,
          noWrap: true,
          color: 'inherit',
          whiteSpace:'normal',
          wordBreak: 'break-word'
        }}
        sx={{ m: 0 }}
      />

      {children?.length > 0 && (
        <ChevronRight sx={{ fontSize: 14, color: 'inherit' }} />
      )}
    </Box>
  </ListItemButton>

  <Popover
    open={Boolean(anchorEl)}
    anchorEl={anchorEl}
    onClose={handlePopoverClose}
    anchorOrigin={{ vertical: 'center', horizontal: 'right' }}
    transformOrigin={{ vertical: 'center', horizontal: 'left' }}
    slotProps={{
      paper: {
        sx: {
          p: 1,
          borderRadius: 2,
          minWidth: 180,
          background: alpha(theme.palette.background.paper, 0.94),
          backdropFilter: 'blur(12px)',
          border: `1px solid ${theme.palette.divider}`,
          boxShadow: theme.customShadows?.z24 || theme.shadows[24],
        },
      },
    }}
  >
	    {children.map((child) => (
	      <ListItemButton
	        key={child.title}
	        component={child.openNewTab ? "a" : RouterLink}
	        {...(child.openNewTab ? { href: child.path, target: "_blank", rel: "noopener noreferrer" } : { to: child.path })}
	        onContextMenu={triggerSessionHandoff}
	        onAuxClick={(event) => {
	          if (event.button === 1) triggerSessionHandoff();
	        }}
	        onMouseDown={(event) => {
	          if (event.button === 1) triggerSessionHandoff();
	        }}
	        onClick={(event) => {
	          if (child.openNewTab) {
	            handleOpenNewTab(event, child.path, handlePopoverClose);
	            return;
	          }
	          handlePopoverClose();
	        }}
		          sx={{
	          px: 2,
	          py: 1.25,
	          borderRadius: 1,
	          color: active(child.path) ? 'primary.main' : 'text.primary',
	          bgcolor: active(child.path) ? 'action.selected' : 'transparent',
	          '&:hover': {
	            bgcolor: active(child.path) ? 'action.selected' : 'action.hover',
	          },
	        }}
	      >
        <ListItemIconStyle>
          {child.icon && child.icon}
        </ListItemIconStyle>
        <ListItemText
          primary={child.title}
          primaryTypographyProps={{ fontSize: 13 }}
        />
      </ListItemButton>
    ))}
  </Popover>
</Box>)}


        </>
      );
    }

    return (
      <>
        {withDisabledTooltip(<ListItemStyle
          disabled={isDisabled}
          onClick={isDisabled ? undefined : handleOpen}
          aria-label={isDisabled ? `${title}: ${disabledMessage}` : title}
          sx={{
            ...(isParentActive && activeRootStyle),
            ...(!isParentActive && open && { bgcolor: 'action.hover', color: 'text.primary' }),
            ...checkHideInXs(isHideInXs),
          }}
        >
          <ListItemIconStyle sx={{ mr: 1.25 }}>{icon && icon}</ListItemIconStyle>
          <ListItemText
            disableTypography
            primary={title}
            primaryTypographyProps={{ sx: { lineHeight: 1.2 } }}
            sx={{ my: 0 }}
          />
          {info && info}
          <Iconify
            icon={open ? 'eva:arrow-ios-downward-fill' : 'eva:arrow-ios-forward-fill'}
            sx={{ width: 16, height: 16, ml: 1 }}
          />
        </ListItemStyle>)}

        <Collapse in={!isDisabled && open} timeout="auto" unmountOnExit>
          <List
            component="div"
            disablePadding
            sx={{
              position: 'relative',
              pl: '14px',
              ml: '17px',
              '--nav-bullet-size': '14px',
              '--nav-item-sub-height': '36px',
              '--nav-bullet-light-color': theme.palette.mode === 'dark' ? theme.palette.grey[700] : theme.palette.grey[300],
              '&::before': {
                top: 0,
                left: 0,
                width: 2,
                content: '""',
                position: 'absolute',
                backgroundColor: 'var(--nav-bullet-light-color)',
                bottom: 'calc(var(--nav-item-sub-height) - 2px - var(--nav-bullet-size) / 2)',
              },
            }}
          >
            {children.map((child) => {
              const isActiveSub = active(child.path);
              return (
                <ListItemStyle
                  key={child.title}
                  component={child.openNewTab ? "a" : RouterLink}
                  {...(child.openNewTab ? { href: child.path, target: "_blank", rel: "noopener noreferrer" } : { to: child.path })}
                  onContextMenu={triggerSessionHandoff}
                  onAuxClick={(event) => {
                    if (event.button === 1) triggerSessionHandoff();
                  }}
                  onMouseDown={(event) => {
                    if (event.button === 1) triggerSessionHandoff();
                  }}
                  onClick={(event) => {
                    if (child.openNewTab) {
                      handleOpenNewTab(event, child.path);
                      return;
                    }
                  }}
                  sx={{
                    position: 'relative',
                    minHeight: 36,
                    height: 36,
                    borderRadius: 1.25,
                    pl: '7px',
                    '&::before': {
                      left: 0,
                      content: '""',
                      position: 'absolute',
                      width: 'var(--nav-bullet-size)',
                      height: 'var(--nav-bullet-size)',
                      backgroundColor: 'var(--nav-bullet-light-color)',
                      WebkitMask:
                        "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='14' height='14' fill='none' viewBox='0 0 14 14'%3E%3Cpath d='M1 1v4a8 8 0 0 0 8 8h4' stroke='%23efefef' stroke-width='2' stroke-linecap='round'/%3E%3C/svg%3E\") 50% 50% / 100% no-repeat",
                      mask:
                        "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='14' height='14' fill='none' viewBox='0 0 14 14'%3E%3Cpath d='M1 1v4a8 8 0 0 0 8 8h4' stroke='%23efefef' stroke-width='2' stroke-linecap='round'/%3E%3C/svg%3E\") 50% 50% / 100% no-repeat",
                      transform: 'translate(calc(var(--nav-bullet-size) * -1), calc(var(--nav-bullet-size) * -0.4))',
                    },
                    ...(!child.openNewTab && isActiveSub && activeSubStyle),
                  }}
                >
                  <ListItemText
                    disableTypography
                    primary={child.title}
                    primaryTypographyProps={{
                      sx: { fontSize: '0.84rem' },
                    }}
                  />
                </ListItemStyle>
              );
            })}
          </List>
        </Collapse>
      </>
    );
  }

  return (
    withDisabledTooltip(<ListItemStyle
      component={isDisabled ? 'div' : openNewTab ? 'a' : RouterLink}
      {...(!isDisabled && (openNewTab ? { href: path, target: '_blank', rel: 'noopener noreferrer' } : { to: path }))}
      disabled={isDisabled}
      aria-label={isDisabled ? `${title}: ${disabledMessage}` : title}
      onContextMenu={isDisabled ? undefined : triggerSessionHandoff}
      onAuxClick={isDisabled ? undefined : (event) => {
        if (event.button === 1) triggerSessionHandoff();
      }}
      onMouseDown={isDisabled ? undefined : (event) => {
        if (event.button === 1) triggerSessionHandoff();
      }}
      onClick={isDisabled ? undefined : (event) => {
        if (openNewTab) {
          handleOpenNewTab(event, path);
          return;
        }
        if (event.ctrlKey || event.metaKey || event.shiftKey) triggerSessionHandoff();
      }}
      sx={{
        ...(!openNewTab && isActiveRoot && activeRootStyle),
        ...checkHideInXs(isHideInXs),
        flexDirection: isCollapsed ? 'column' : 'row',
        justifyContent: isCollapsed ? 'center' : 'flex-start',
        alignItems: 'center',
        textAlign: isCollapsed ? 'center' : 'left',
        py: isCollapsed ? 1.25 : 0,
        height: isCollapsed ? 68 : 44,
      }}
    >
      <ListItemIconStyle sx={{ mb: isCollapsed ? 0.5 : 0, mr: isCollapsed ? 0 : 1.25 }}>{icon && icon}</ListItemIconStyle>
      <ListItemText
        disableTypography
        primary={title}
        primaryTypographyProps={{ sx: { lineHeight: 1.2 } }}
        sx={{
          my: 0,
          fontSize: isCollapsed ? '0.75rem' : '0.875rem',
          display: 'block',
          mt: isCollapsed ? 0.5 : 0,
          whiteSpace: 'normal',
          wordBreak: 'break-word',
          lineHeight: 1.2,
        }}
      />
      {info && info}
    </ListItemStyle>)
  );
}

NavItem.propTypes = {
  item: PropTypes.object,
  active: PropTypes.func,
  userType: PropTypes.number,
  isCollapsed: PropTypes.bool,
};

export default function NavSection({ navConfig, userData, isCollapsed, ...other }) {
  
  const { pathname } = useLocation();
  const match = (path) => (path ? !!matchPath({ path }, pathname) : false);

  const clientId = Number(userData.client_id);
  const userType = Number(userData.user_type);

  // Hide menu entries a restricted client is not allowed to open.
  const isNavItemAllowed = (item) => isPageAllowedForClient(clientId, item.path);

  let verificationStatus = localStorage.getItem('verification_status');
  verificationStatus = verificationStatus === 'null' ? false : true;

  const [filteredNavConfig, setFilteredNavConfig] = useState([]);
  const [updatedDeploymentMenuItems, setupdatedDeploymentMenuItems] = useState([]);
  const [updatedMenuItems, setupdatedMenuItems] = useState([]);

  useEffect(() => {

    const newNavConfig = [];

    if (userType === 8) {
      const settingsItem = navConfig.find((item) => item.title === 'Settings');
      const opsDashboardItem = navConfig.find((item) => item.title === 'Dashboard');
      const eventsReportItem = navConfig.find((item) => item.title === 'Event Reports');

      if (opsDashboardItem) newNavConfig.push(opsDashboardItem);
      if (eventsReportItem) newNavConfig.push(eventsReportItem);
      if (settingsItem) {
        newNavConfig.push(...settingsItem.children.filter((child) => child.title === 'Add Agent'));
        newNavConfig.push(...settingsItem.children.filter((child) => child.title === 'client Assign'));
      }

      setFilteredNavConfig(newNavConfig);
    } else {
      //console.log(navConfig)
      const isMergeClient = localStorage.getItem('Type');
      let MenuItems =
        isMergeClient === null || isMergeClient === 'null'
          ? navConfig.filter((item) => item.title !== 'Analytics' && item.title !== 'Add Camera')
          : navConfig.filter(
              (item) =>
                item.title !== 'Analytics' &&
                item.title !== 'Add Camera' &&
                item.title !== 'Contacts'
            );

      const clientId = localStorage.getItem('client_id')
      const Type = localStorage.getItem('Type');
      const EnvironmentType = localStorage.getItem('EnvironmentType');
      const clientType = localStorage.getItem('client_type');
      const ID = localStorage.getItem('ID')

      const isAdminUnavailable =
        clientId === '1422' &&
        EnvironmentType?.toLowerCase() === 'demo' &&
        clientType?.toUpperCase() === 'US';

      if (isAdminUnavailable) {
        const unavailableItems = ['Admin', 'Live', 'Playback', 'Reports'];

        MenuItems = MenuItems.map((item) =>
          unavailableItems.includes(item.title)
            ? { ...item, disabledMessage: 'This feature is not available for this client.' }
            : item
        );
      }

      const storedProperty = localStorage.getItem('Location');
      const AssignClient = localStorage.getItem('AssignClient');
      const ShareLocation = localStorage.getItem('ShareLocation');

      const userType = localStorage.getItem('user_type');


      // if(!['1266', '1302', '1316', '1320', '1357'].includes(clientId)){
      if((EnvironmentType === "general") || ['1262','1273'].includes(clientId)){
        //"Admin"
        
        if(userType === "6" || userType === '41' || userType === '42'){
          const allowedPaths = ['/invoiceReport', '/creditSettings','/price','/invoiceGroupingTIime'];
          MenuItems = MenuItems.filter(item => item && item.path && allowedPaths.includes(item.path));
        }
        else{
          MenuItems = MenuItems.map(item => {
            if (item.title === 'Admin' && item.children) {
                  return {
                    ...item,
                    children: item.children.filter(child => {
                    let newAdminItem = child.title !== 'Operations Team';
                    if (['share', 'sub'].includes(Type)) {
                    newAdminItem = child.title !== 'Operations Team' && child.title !== 'Share Login Access' && child.title !== 'Protocols';
                  } 
                  return newAdminItem;
                })
              };
            }
            return item;
          });
        }
      }

      // if(['1266', '1302', '1316', '1320', '1357'].includes(clientId)){
      if((EnvironmentType === "demo") && !['1262', '1273'].includes(clientId)){
        MenuItems = MenuItems.map(item => {
          if (item.title === 'Admin' && item.children) {
            return {
              ...item,
              children: item.children.filter(child => {
                // Only return child items if Type is 'share', otherwise return false (no children)
                if (['share', 'sub', 'merge'].includes(Type)) {
                  // If Type is 'share', hide 'Operations Team', 'Share Login Access', and 'Protocols'
                  return (
                    child.title !== 'Operations Team' &&
                    child.title !== 'Share Login Access' &&
                    child.title !== 'Protocols'
                  );
                }
                // For any other Type, do not return any children
                return true;
              })
            };
          }
          return item;
        });
      }

      if((AssignClient!=='' && storedProperty!=='' && (clientId === '1353' || ID === '1388' || ID === '1364'))) {
        MenuItems = MenuItems.filter(item => item.title !== 'Playback');
      }

      if(ShareLocation && ShareLocation !== ''){
        MenuItems = MenuItems.filter(item => item.title !== 'Playback');
      }

      setupdatedMenuItems(MenuItems);

      const deploymentMenuItems = navConfig.filter(
        (item) => item.title !== 'Dashboard' && item.title !== 'Event Reports'
      );

      let items = []
      if(userType === 40){
          items = deploymentMenuItems.map((item) => {
          if (item.title === 'Settings' && item.children) {
            return {
              ...item,
              children: item.children.filter(
                (child) => child.title !== 'Protocols' && child.title !== 'Email Reports' && child.title !== 'Email Timings' && child.title !== 'Add Agent' && child.title !== 'client Assign' && child.title !== 'Models' && child.title !== 'Combination Models' && child.title !== 'Camera Models/Combo Config'
              ),
            };
          }
          return item;
        });
      }
      else{
        items = deploymentMenuItems.map((item) => {
        if (item.title === 'Settings' && item.children) {
          return {
            ...item,
            children: item.children.filter(
              (child) => child.title !== 'Add Agent' && child.title !== 'client Assign'
            ),
          };
        }
        return item;
      });
      }

      console.log("deploymentMenuItems", items);
      setupdatedDeploymentMenuItems(items);
    }
  }, [userType, navConfig]);

  return (
    <Box {...other}>
      <List disablePadding sx={{ p: 1 }}>
        {((!verificationStatus && (userType === 9 || userType === 40)) ||
          (verificationStatus && userType === 2) ||
          [7, 30, 31, 32, 33, 35, 37, 38].includes(userType)) &&
          updatedDeploymentMenuItems
            .filter(
              (item) =>
                (item.positiveAccess.length === 0 ||
                  (item.positiveAccess.includes(clientId) &&
                    (item.positiveUserRoles.includes(userType) ||
                      item.positiveUserRoles.length === 0))) &&
                !item.negativeUserRoles.includes(userType) &&
                !item.negativeAccess.includes(clientId) &&
                isNavItemAllowed(item)
            )
            .map((item) => (
              <NavItem
                key={item.title}
                item={item}
                active={match}
                userType={userType}
                isCollapsed={isCollapsed}
              />
            ))}

        {!verificationStatus &&
          userType === 2 &&
          updatedMenuItems
            .filter(
              (item) =>
                (item.positiveAccess.length === 0 ||
                  (item.positiveAccess.includes(clientId) &&
                    (item.positiveUserRoles.includes(userType) ||
                      item.positiveUserRoles.length === 0))) &&
                !item.negativeUserRoles.includes(userType) &&
                !item.negativeAccess.includes(clientId) &&
                isNavItemAllowed(item)
            )
            .map((item) => (
              <NavItem
                key={item.title}
                item={item}
                active={match}
                userType={userType}
                isCollapsed={isCollapsed}
              />
            ))}

        {!verificationStatus &&
          userType === 8 &&
          filteredNavConfig
            .filter(
              (item) =>
                (item.positiveAccess.length === 0 ||
                  (item.positiveAccess.includes(clientId) &&
                    (item.positiveUserRoles.includes(userType) ||
                      item.positiveUserRoles.length === 0))) &&
                !item.negativeUserRoles.includes(userType) &&
                !item.negativeAccess.includes(clientId) &&
                isNavItemAllowed(item)
            )
            .map((item) => (
              <NavItem
                key={item.title}
                item={item}
                active={match}
                userType={userType}
                isCollapsed={isCollapsed}
              />
            ))}
          {(userType === 6 || userType === 41 || userType === 42) &&
            updatedMenuItems.filter(isNavItemAllowed).map(
            (item) =>              
                <NavItem
                  key={item.title}
                  item={item}
                  active={match}
                  userType={userType}
                  isCollapsed={isCollapsed}
                />)
          }
      </List>
    </Box>
  );
}

NavSection.propTypes = {
  navConfig: PropTypes.array,
  userData: PropTypes.object,
  isCollapsed: PropTypes.bool,
};
